import { atom } from 'jotai';
import { RESET } from 'jotai/utils';
import { CurrentImageProps, SegmentProps, VisibilityBoxMode } from '@fabric-web/3d-viewer';
import { AbyssViewerState, PartSummary, PointSize } from '@/types';
import {
  abyssViewerState,
  areSphericalsVisible,
  selectedPartIds,
  structureRelationships,
  structures,
  panelsMode,
  currentVisibilityBox,
  zones,
  zoneSegmentsHeight,
  shouldShowDemarcationOfZones,
  viewerCurrentSpherical,
  viewerNextCurrentSpherical,
  pointSizeAtom,
} from '../atoms';
import { DEFAULT_COLORS, DELETED_TAG_ASSEMBLY_NAME, NO_TAG_ASSEMBLY_NAME } from '@/constants';
import { notEmpty } from '@/utils';
import * as analysisState from '@/components/Analysis/state';

export const structureNameByStructureId = atom((get) => (structureId: string | undefined) => {
  if (structureId === undefined) {
    return undefined;
  }
  return get(structures).find((structure) => structure.id === structureId)?.name;
});

export const selectedSpherical = atom(
  (get) => get(viewerCurrentSpherical),
  (get, set, newValue: CurrentImageProps | undefined) => {
    set(viewerNextCurrentSpherical, newValue);
  }
);

export const annotationIdsForSelectedPart = atom((get) => {
  const partIds = get(selectedPartIds);
  if (partIds.length === 0) {
    return [];
  }
  const annotationIds = partIds.flatMap((partId) => {
    const annotationIdsForPart = get(structureRelationships)
      ?.byPartId?.get(partId)
      ?.annotationIds.values();
    return annotationIdsForPart ? [...annotationIdsForPart] : [];
  });
  return annotationIds;
});

export const selectedPartSummaries = atom<PartSummary[]>((get) => {
  const result: PartSummary[] = [];
  const partIds = get(selectedPartIds);
  const relationships = get(structureRelationships);

  if (!relationships) return [];

  const partById = relationships.byPartId;
  const assemblyById = relationships.byAssemblyId;

  partIds.forEach((partId) => {
    const part = partById.get(partId);
    if (!part) return;
    const assembly = part.assemblyId ? assemblyById.get(part.assemblyId) : undefined;

    result.push({
      id: partId,
      assemblyId: part.assemblyId,
      assemblyTagName: assembly?.tagName,
      partClassName: part.class,
      isHidden: part.isHidden,
      isReviewed: part.isReviewed,
    });
  });
  return result;
});

export const inSphericalView = atom<boolean>((get) => get(selectedSpherical) !== undefined);

const previousSphericalForAbyssMode = atom<CurrentImageProps | undefined>(undefined);

const previousAreSphericalsVisibleForAbyssMode = atom<boolean>(true);

export const setAbyssViewerState = atom(
  (get) => get(abyssViewerState),

  (get, set, newValue: AbyssViewerState) => {
    // WHATS DEFAULT VALUE
    if (Object.is(newValue, RESET)) {
      set(panelsMode, RESET);
      set(abyssViewerState, RESET);
      return;
    }

    const { mode: currentMode } = get(abyssViewerState);

    // Entering/exiting isolate part mode should update the panel mode appropriately
    if (currentMode === 'IsolatePart') {
      set(panelsMode, 'Default');
      set(selectedSpherical, get(previousSphericalForAbyssMode));
      set(areSphericalsVisible, get(previousAreSphericalsVisibleForAbyssMode));
    } else if (newValue.mode === 'IsolatePart') {
      set(panelsMode, 'IsolatePart');
      set(previousSphericalForAbyssMode, get(selectedSpherical));
      set(previousAreSphericalsVisibleForAbyssMode, get(areSphericalsVisible));
      set(areSphericalsVisible, false);
    }

    // Switching abyss viewer mode should force any custom visibility box to finish editing
    if (get(currentVisibilityBox).mode === VisibilityBoxMode.Adjusting) {
      set(currentVisibilityBox, (current) => ({ ...current, mode: VisibilityBoxMode.Enabled }));
    }

    set(abyssViewerState, {
      ...newValue,
      previousMode: currentMode,
    });
  }
);

const assemblyIdsForSelectedParts = atom<string[]>((get) => {
  const partIds = get(selectedPartIds);
  if (partIds.length === 0) {
    return [];
  }

  const assemblyIds = partIds
    .map((partId) => get(structureRelationships)?.byPartId?.get(partId)?.assemblyId)
    .filter(notEmpty);
  return assemblyIds;
});

// Returns an assembly id if all selected parts have the same assembly id, otherwise returns undefined
export const singleAssemblyIdForSelectedParts = atom((get) => {
  const assemblyIds = get(assemblyIdsForSelectedParts);
  if (assemblyIds.every((assemblyId) => assemblyId === assemblyIds[0])) {
    return assemblyIds[0];
  }
  return undefined;
});

export const singleAssemblyInfoForSelectedParts = atom((get) => {
  const selectedAssemblyId = get(singleAssemblyIdForSelectedParts);
  const byAssemblyId = get(structureRelationships)?.byAssemblyId;
  return selectedAssemblyId && byAssemblyId ? byAssemblyId.get(selectedAssemblyId) : undefined;
});

export const allAssemblies = atom((get) => {
  const byAssemblyId = get(structureRelationships)?.byAssemblyId;
  if (byAssemblyId) {
    return byAssemblyId.map(
      ({ tagName, isNewlyAdded, originalTagname, superCategory }, assemblyId) => ({
        id: assemblyId,
        tagName,
        originalTagname,
        superCategory,
        isNewlyAdded,
      })
    );
  }
  return [];
});

export const getSegmentsByCurrentZones = atom((get): SegmentProps[] => {
  const zonesForStructure = get(zones);
  const zoneSegmentsHeightState = get(zoneSegmentsHeight);
  const shouldShowDemarcationOfZonesState = get(shouldShowDemarcationOfZones);
  if (shouldShowDemarcationOfZonesState && zonesForStructure?.length > 0) {
    return zonesForStructure.map((zone) => ({
      name: zone.id,
      label: zone.name,
      bounds: {
        min: [zone.box.min.x, zone.box.min.y, zone.box.min.z],
        max: [zone.box.max.x, zone.box.max.y, zone.box.min.z + zoneSegmentsHeightState],
      },
      fillColor: DEFAULT_COLORS.segmentsFillColour,
      fillOpacity: 0.05,
      lineColor: DEFAULT_COLORS.segmentsLineColour,
      lineOpacity: 0.4,
      lineWidth: 1,
    }));
  }
  return [];
});

export const assemblyWithDeletedTag = atom((get) => {
  const structureRelationshipsState = get(structureRelationships);
  const assemblies = structureRelationshipsState?.byAssemblyId || [];
  const selected = assemblies.find((assembly) => {
    return assembly.tagName === DELETED_TAG_ASSEMBLY_NAME;
  });
  return selected?.[0];
});

export const assemblyWithUntaggedTag = atom((get) => {
  const structureRelationshipsState = get(structureRelationships);
  const assemblies = structureRelationshipsState?.byAssemblyId || [];

  const selected = assemblies.find((assembly) => {
    return assembly.tagName === NO_TAG_ASSEMBLY_NAME;
  });
  return selected?.[0];
});

/**
 * Point size selector
 * This selector provides separate point size states for **viewer mode** and **spherical mode**.
 * It simplifies component logic by automatically determining and returning the appropriate point size
 * based on the active mode, and ensures that updates are directed to the correct state without requiring
 * changes in consuming components.
 */
export const pointSize = atom(
  (get) => {
    const internalPointSize = get(pointSizeAtom);
    const currentSphericalAnalysis = get(analysisState.viewerCurrentSpherical);

    if (currentSphericalAnalysis) return internalPointSize.spherical;
    return internalPointSize.viewer;
  },

  (get, set, newValue) => {
    const internalPointSize = get(pointSizeAtom);
    const currentSphericalAnalysis = get(analysisState.viewerCurrentSpherical);

    if (typeof newValue !== 'number') return;

    const newPointSize: PointSize = (() => {
      if (currentSphericalAnalysis) {
        return { ...internalPointSize, spherical: newValue };
      }

      return { ...internalPointSize, viewer: newValue };
    })();

    set(pointSizeAtom, newPointSize);
  }
);
